import { Component } from '@theme/component';

/**
 * MOXIE: reusado por custom_hero-slider.liquid y custom_collection-carousel.liquid. Crossfade puro
 * (is-active), sin scroll/translate; peekPrevious/peekNext clonan el slide adyacente
 * (media + copy + CTA): peek siguiente alineado al inicio (izq.), peek anterior al final (der.).
 * Peeks reproducen video (muted) cuando el carrusel es visible.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} slides
 * @property {HTMLElement} [peekPrevious]
 * @property {HTMLElement} [peekNext]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLElement} [current]
 *
 * @extends {Component<Refs>}
 */
export class HeroFadeSlider extends Component {
  /** @type {number} */
  #activeIndex = 0;

  /** @type {number | null} */
  #autoplayTimer = null;

  /** @type {number | null} */
  #autoplayIntervalMs = null;

  /** @type {MediaQueryList | null} */
  #peekVideoMediaQuery = null;

  /** @type {(() => void) | null} */
  #peekVideoMediaQueryListener = null;

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {boolean} */
  #isVisible = true;

  connectedCallback() {
    super.connectedCallback();

    const slides = this.refs.slides ?? [];
    const initialSlide = slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.#activeIndex = initialSlide > -1 ? initialSlide : 0;
    this.#render();
    this.#setupVisibilityObserver();
    this.#isVisible = false;

    const autoplaySpeed = Number(this.getAttribute('autoplay'));
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (autoplaySpeed > 0 && !prefersReducedMotion) {
      this.play(autoplaySpeed * 1000);
    }

    if (this.refs.peekPrevious || this.refs.peekNext) {
      this.#peekVideoMediaQuery = window.matchMedia('(max-width: 749px)');
      this.#peekVideoMediaQueryListener = () => this.#refreshPeeks();
      this.#peekVideoMediaQuery.addEventListener('change', this.#peekVideoMediaQueryListener);
      this.#bindPeekNavigation();
    }

    document.addEventListener('visibilitychange', this.#handleDocumentVisibility);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.pause();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;

    if (this.#peekVideoMediaQuery && this.#peekVideoMediaQueryListener) {
      this.#peekVideoMediaQuery.removeEventListener('change', this.#peekVideoMediaQueryListener);
    }

    document.removeEventListener('visibilitychange', this.#handleDocumentVisibility);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'ArrowRight') this.next(event);
    if (event.key === 'ArrowLeft') this.previous(event);
  };

  #handleDocumentVisibility = () => {
    if (document.hidden) {
      this.#pauseAllVideos();
      this.#clearAutoplayTimer();
      return;
    }

    this.#syncVideos();

    if (this.#autoplayIntervalMs) {
      this.play(this.#autoplayIntervalMs);
    }
  };

  get slideCount() {
    return this.refs.slides?.length ?? 0;
  }

  /**
   * Selects a slide by index (wraps around).
   * @param {number} index
   * @param {Event} [event]
   */
  select(index, event) {
    event?.preventDefault();
    const count = this.slideCount;
    if (!count) return;

    const nextIndex = ((Number(index) % count) + count) % count;
    if (nextIndex === this.#activeIndex) return;

    this.#activeIndex = nextIndex;
    this.#render();
  }

  /** @param {Event} [event] */
  next(event) {
    event?.preventDefault();
    this.select(this.#activeIndex + 1);
  }

  /** @param {Event} [event] */
  previous(event) {
    event?.preventDefault();
    this.select(this.#activeIndex - 1);
  }

  /**
   * Starts autoplay.
   * @param {number} [interval] - Milliseconds between slides.
   */
  play(interval = 5000) {
    this.#autoplayIntervalMs = interval;
    this.#clearAutoplayTimer();
    this.#autoplayTimer = window.setInterval(() => this.next(), interval);
    this.setAttribute('autoplay-running', '');
  }

  pause() {
    this.#autoplayIntervalMs = null;
    this.#clearAutoplayTimer();
    this.removeAttribute('autoplay-running');
  }

  #clearAutoplayTimer() {
    if (this.#autoplayTimer) {
      window.clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }

  #setupVisibilityObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.#visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.#isVisible = entry?.isIntersecting ?? true;

        if (this.#isVisible) {
          this.#syncVideos();
          return;
        }

        this.#pauseAllVideos();
        this.#clearAutoplayTimer();
      },
      { threshold: 0.1 }
    );

    this.#visibilityObserver.observe(this);
  }

  #render() {
    const { slides = [], dots = [], current, peekPrevious, peekNext } = this.refs;
    const count = slides.length;
    if (!count) return;

    const previousIndex = (this.#activeIndex - 1 + count) % count;
    const nextIndex = (this.#activeIndex + 1) % count;

    for (const [index, slide] of slides.entries()) {
      const isActive = index === this.#activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    }

    this.#syncVideos();

    for (const [index, dot] of dots.entries()) {
      dot.setAttribute('aria-selected', String(index === this.#activeIndex));
    }

    if (current) current.textContent = String(this.#activeIndex + 1);

    this.#updatePeek(peekPrevious, slides[previousIndex], previousIndex);
    this.#updatePeek(peekNext, slides[nextIndex], nextIndex);
  }

  #refreshPeeks() {
    const { slides = [], peekPrevious, peekNext } = this.refs;
    const count = slides.length;
    if (!count) return;

    const previousIndex = (this.#activeIndex - 1 + count) % count;
    const nextIndex = (this.#activeIndex + 1) % count;

    if (peekPrevious) peekPrevious.replaceChildren();
    if (peekNext) peekNext.replaceChildren();

    this.#updatePeek(peekPrevious, slides[previousIndex], previousIndex);
    this.#updatePeek(peekNext, slides[nextIndex], nextIndex);
  }

  #syncVideos() {
    const { slides = [] } = this.refs;

    if (!this.#isVisible || document.hidden) {
      this.#pauseAllVideos();
      return;
    }

    for (const [index, slide] of slides.entries()) {
      const isActive = index === this.#activeIndex;

      for (const video of slide.querySelectorAll('video')) {
        const isRendered = video.getClientRects().length > 0;
        if (isActive && isRendered) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    }

    for (const clone of this.querySelectorAll('[data-peek-clone]')) {
      this.#playPeekCloneVideos(clone);
    }
  }

  #pauseAllVideos() {
    for (const video of this.querySelectorAll('video')) {
      video.pause();
    }
  }

  /**
   * @param {HTMLElement} [peekElement]
   * @param {HTMLElement} [sourceSlide]
   * @param {number} sourceIndex
   */
  #updatePeek(peekElement, sourceSlide, sourceIndex) {
    if (!peekElement || !sourceSlide) return;

    if (!peekElement.classList.contains('collection-fade-carousel__peek')) {
      const imageSrc = sourceSlide.getAttribute('data-peek-src');
      if (imageSrc) {
        peekElement.style.setProperty('--peek-image', `url("${imageSrc}")`);
      }
      return;
    }

    const sourceKey = String(sourceIndex);
    const existing = peekElement.querySelector('[data-peek-clone]');

    if (existing instanceof HTMLElement && existing.dataset.peekSourceId === sourceKey) {
      this.#playPeekCloneVideos(existing);
      return;
    }

    peekElement.replaceChildren();
    peekElement.style.removeProperty('--peek-image');

    const clone = /** @type {HTMLElement} */ (sourceSlide.cloneNode(true));
    clone.classList.add('collection-fade-carousel__peek-slide');
    clone.classList.remove('is-active');
    clone.dataset.peekClone = 'true';
    clone.dataset.peekSourceId = sourceKey;
    clone.setAttribute('aria-hidden', 'true');
    this.#stripCloneAttributes(clone);
    this.#optimizePeekClone(clone);

    peekElement.appendChild(clone);
    this.#playPeekCloneVideos(clone);
  }

  #bindPeekNavigation() {
    const { peekPrevious, peekNext } = this.refs;

    if (peekPrevious && !peekPrevious.dataset.peekNavBound) {
      peekPrevious.dataset.peekNavBound = 'true';
      peekPrevious.addEventListener('click', () => this.previous());
    }

    if (peekNext && !peekNext.dataset.peekNavBound) {
      peekNext.dataset.peekNavBound = 'true';
      peekNext.addEventListener('click', () => this.next());
    }
  }

  /**
   * @param {HTMLElement} clone
   */
  #stripCloneAttributes(clone) {
    const elements = [clone, ...clone.querySelectorAll('*')];

    for (const element of elements) {
      element.removeAttribute('ref');
      element.removeAttribute('id');
      element.removeAttribute('data-peek-src');
      element.removeAttribute('data-peek-video');
      element.removeAttribute('data-peek-video-mobile');
    }

    for (const link of clone.querySelectorAll('.hero-fade-slide__link')) {
      link.remove();
    }
  }

  /**
   * @param {HTMLElement} clone
   */
  #optimizePeekClone(clone) {
    const isMobile = this.#peekVideoMediaQuery?.matches ?? window.matchMedia('(max-width: 749px)').matches;

    for (const wrapper of clone.querySelectorAll('.collection-slide__media-wrapper')) {
      const isDesktopWrapper = wrapper.classList.contains('collection-slide__media-wrapper--desktop');
      const isMobileWrapper = wrapper.classList.contains('collection-slide__media-wrapper--mobile');
      const isDualWrapper = isDesktopWrapper && isMobileWrapper;

      if (isDualWrapper) continue;

      if (isMobile && isDesktopWrapper && !isMobileWrapper) {
        wrapper.remove();
        continue;
      }

      if (!isMobile && isMobileWrapper && !isDualWrapper) {
        wrapper.remove();
      }
    }

    for (const video of clone.querySelectorAll('video')) {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'none';
    }
  }

  /**
   * @param {HTMLElement} clone
   */
  #playPeekCloneVideos(clone) {
    if (!this.#isVisible || document.hidden) return;

    for (const video of clone.querySelectorAll('video')) {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.play().catch(() => {});
    }
  }
}

if (!customElements.get('hero-fade-slider')) {
  customElements.define('hero-fade-slider', HeroFadeSlider);
}
